import { connect } from 'react-redux';
import type { ProductCarouselProps } from '@noths/polaris-client-ribbons-design-system';
import { ProductCarousel } from '@noths/polaris-client-ribbons-design-system';
import type { RecommendedProduct } from '@noths/polaris-dev-ts-types';

import type { Placement } from 'src/constants/recommendations';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { transformRecommendedProductToProductCarouselProduct } from 'src/utils/transformRecommendedProductToProductCarouselProduct';
import {
  trackRecommendedProductClick,
  trackRecommendedProductsCarouselNavigation,
  trackRecommendedProductsImpression,
} from '../tracking/recommendedProductShelfEvents';

interface RecommendedProductsShelfContainerProps extends Pick<ProductCarouselProps, 'lazyImages'> {
  enableFreeDomesticDeliveryLabels?: boolean;
  hideLabelIcons?: boolean;
  placementType: Placement;
  products: RecommendedProduct[] | null;
  title?: string;
}

const mapStateToProps = (
  { personalisation, richRelevance, userConfiguration }: ReduxApplicationState,
  {
    placementType,
    products: recommendedProducts,
    ...props
  }: RecommendedProductsShelfContainerProps,
): ProductCarouselProps => {
  const placement = richRelevance.placements[placementType];
  const { currencyCode } = userConfiguration;
  const products = recommendedProducts
    ? recommendedProducts.map((product: RecommendedProduct) =>
        transformRecommendedProductToProductCarouselProduct(product, currencyCode),
      )
    : null;

  if (!placement) {
    return {
      ...props,
      currency: currencyCode,
      products,
    };
  }

  const onVisible = () => {
    const { placementStrategy = '', products } = placement;

    if (products === null || products.length === 0) {
      return;
    }

    trackRecommendedProductsImpression({
      personalisation,
      products,
      placementStrategy,
      placementType,
    });
  };

  const onProductClick = (evt: React.MouseEvent, { index }: { index: number }) => {
    const { placementStrategy = '', products } = placement;

    if (products === null || products.length === 0) {
      return;
    }

    trackRecommendedProductClick(evt, {
      personalisation,
      product: products[index],
      placementStrategy,
      productIndex: index,
      placementType,
    });
  };

  const onNavigation = (swiped: boolean, control: 'next' | 'prev') => {
    const { placementStrategy = '' } = placement;
    trackRecommendedProductsCarouselNavigation({
      placementStrategy,
      placementType,
      navigation: {
        type: swiped ? 'Scroll' : 'Arrow Click',
        direction: control === 'next' ? 'Right' : 'Left',
      },
    });
  };

  return {
    ...props,
    currency: currencyCode,
    products,
    onVisible,
    onProductClick,
    onNavigation,
  };
};

export const RecommendedProductsShelfContainer = connect(mapStateToProps)(ProductCarousel);
